import { fileUrl, imgUrl, imgSrcSet } from "@/lib/api";

export function parseSize(s) {
  if (!s) return null;
  const m = String(s).toLowerCase().split("x");
  if (m.length !== 2) return null;
  const w = parseInt(m[0], 10), h = parseInt(m[1], 10);
  return isFinite(w) && isFinite(h) ? [w, h] : null;
}

export function labelFor(color) {
  return { black: "Black", "dark-wood": "Dark Wood", "natural-wood": "Natural Wood", white: "White" }[color] || color;
}
export function swatchClass(color) {
  return {
    black: "bg-neutral-900 border-neutral-800",
    "dark-wood": "bg-[#4a3320] border-[#2e1f13]",
    "natural-wood": "bg-[#c9a56a] border-[#a37c46]",
    white: "bg-white border-neutral-300",
  }[color] || "bg-neutral-400";
}
export function materialFrameClass(color) {
  return {
    "natural-wood": "bg-[linear-gradient(135deg,#d9b98a,#b8925c_55%,#d9b98a)]",
    "dark-wood": "bg-[linear-gradient(135deg,#5a3a24,#2f1d12_60%,#5a3a24)]",
    white: "bg-[linear-gradient(135deg,#f6f3ee,#e6e1d8_60%,#f6f3ee)]",
  }[color] || "bg-[linear-gradient(135deg,#2a2622,#0e0d0c_60%,#2a2622)]";
}

export function frameBorderClass(color) {
  return {
    black: "border-neutral-900",
    "dark-wood": "border-[#4a3320]",
    "natural-wood": "border-[#c9a56a]",
    white: "border-white",
  }[color] || "border-transparent";
}

// Renders the artwork inside a material-appropriate mockup surrounding.
export function ArtMockup({ art, size, material, frame }) {
  const src = fileUrl(art.image_urls?.[0]);
  const [w, h] = parseSize(size?.size) || [16, 20];
  const aspect = `${w} / ${h}`;
  const materialClass = {
    poster: "shadow-[0_18px_50px_-16px_rgba(30,20,10,0.25)]",
    canvas: "shadow-[0_28px_60px_-20px_rgba(30,20,10,0.4),inset_0_0_0_2px_rgba(255,255,255,0.15)] ring-1 ring-black/10",
    metal: "shadow-[0_20px_50px_-18px_rgba(30,20,10,0.5)] ring-1 ring-white/20 saturate-[1.1] brightness-[1.05]",
    wood: "shadow-[0_20px_50px_-18px_rgba(70,40,20,0.55)] ring-2 ring-[#c9a56a]/40 sepia-[0.08]",
    acrylic: "shadow-[0_24px_60px_-18px_rgba(30,20,10,0.5)] ring-4 ring-white/40 contrast-[1.05] saturate-[1.1]",
    fine_art: "shadow-[0_18px_50px_-16px_rgba(30,20,10,0.25)] ring-1 ring-black/5",
    foam: "shadow-[0_14px_40px_-14px_rgba(30,20,10,0.35)] ring-[6px] ring-white",
    framed_canvas: "shadow-[0_28px_60px_-20px_rgba(30,20,10,0.5)]",
    framed_fine_art: "shadow-[0_28px_60px_-20px_rgba(30,20,10,0.5)]",
    mounted_fine_art: "shadow-[0_28px_60px_-20px_rgba(30,20,10,0.5)]",
    mounted_framed_poster: "shadow-[0_28px_60px_-20px_rgba(30,20,10,0.5)]",
    hanger: "shadow-[0_18px_50px_-16px_rgba(30,20,10,0.3)]",
  }[material || "poster"] || "shadow-[0_18px_50px_-16px_rgba(30,20,10,0.25)]";
  const blackFramed = ["framed_canvas", "framed_fine_art", "mounted_fine_art", "mounted_framed_poster"].includes(material);
  return (
    <div className="art-image-wrap bg-cream-soft relative flex items-center justify-center p-6 md:p-10" data-testid="art-mockup-wrap">
      <div className="relative" style={{ width: "min(100%, 560px)", aspectRatio: aspect }}>
        {material === "canvas" && (
          <div className="absolute -inset-3 bg-gradient-to-br from-neutral-200 to-neutral-400 rounded-[2px]" />
        )}
        {material === "wood" && (
          <div className="absolute -inset-1 bg-[linear-gradient(90deg,#c9a56a,#a37c46,#c9a56a)]" />
        )}
        {blackFramed && (
          <div className={`absolute -inset-4 shadow-[inset_0_0_0_2px_rgba(255,255,255,0.06)] ${materialFrameClass(frame?.frame_color)}`} data-testid={`mockup-${frame?.frame_color || "black"}-frame`} />
        )}
        {material === "hanger" && (
          <>
            <div className="absolute -top-2 left-0 right-0 h-2.5 bg-[#1a1816] rounded-sm" />
            <div className="absolute -bottom-2 left-0 right-0 h-2.5 bg-[#1a1816] rounded-sm" />
          </>
        )}
        <img
          src={imgUrl(src, 1280)}
          srcSet={imgSrcSet(src)}
          sizes="(min-width: 1024px) 55vw, 100vw"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          alt={art.title}
          className={`relative w-full h-full object-cover ${materialClass || ""}`}
          data-testid="art-mockup-image"
        />
        {frame && material === "poster" && (
          <div className={`pointer-events-none absolute inset-0 border-[16px] ${frameBorderClass(frame.frame_color)}`}></div>
        )}
        {/* Size scale caption */}
        <div className="absolute -bottom-8 left-0 right-0 text-center text-[10px] uppercase tracking-[0.22em] text-mute" data-testid="mockup-size-caption">
          {size?.size ? `${size.size} in · ${(w * 2.54).toFixed(0)}×${(h * 2.54).toFixed(0)} cm` : ""}
        </div>
      </div>
    </div>
  );
}
